import React, {FC, useContext, useEffect, useLayoutEffect} from 'react';

import {cn} from '@bem-react/classname';
import {Loader} from '@gravity-ui/uikit';
import {QuestionImageSize} from 'common/constants/survey';
import {isEmpty, isNil} from 'lodash';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {FooterContext} from 'ui/contexts/Footer';
import {withErrorBoundary} from 'ui/hocs/withAppProviders';
import useDocumentTitle from 'ui/hooks/useDocumentTitle';
import {useFormUrl} from 'ui/hooks/useFormUrl';
import usePrepareData from 'ui/hooks/usePrepareData';
import useStyles from 'ui/hooks/useStyles';
import {actions, selectors, useAppDispatch, useAppSelector} from 'ui/store';
import {sendPageInfo} from 'ui/utils/postMessages';
import redirectToExternalUrl from 'ui/utils/redirectToExternalUrl';

import Integrations from '../Integrations';
import Promo from '../Promo';
import Results from '../Results';
import ShareComponent from '../Share';
import Stats from '../Stats';
import SuccessMessage from '../SuccessMessage';
import {getSurveyId} from '../SurveyPage/SurveyPage.utils';

import {defaultPaymentParams} from './SuccessPage.constants';

import './SuccessPage.scss';

const {selectSuccess} = selectors;
const {prepareSuccess, prepareIntegrations} = actions;

export const cnSuccessPage = cn('SuccessPage');
export const {
    iframe,
    shareOnSuccess,
    routes: {paymentUrl},
} = window.YC;

const Share = withErrorBoundary(ShareComponent);

const SuccessPage: FC = () => {
    const {surveyId = ''} = useParams();
    const [searchParams] = useSearchParams();
    const answerKey = searchParams.get('akey');
    const shareResult = searchParams.get('share');

    const formUrl = useFormUrl();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const isPreparedData = usePrepareData(
        [
            prepareSuccess({surveyId: getSurveyId(surveyId), answerKey}),
            prepareIntegrations({answerKey}),
        ],
        dispatch,
    );

    const successData = useAppSelector(selectSuccess);

    const {setFooter} = useContext(FooterContext);

    useDocumentTitle(successData.name);

    useStyles(successData.styles);

    useLayoutEffect(() => {
        if (iframe && isPreparedData && !isNil(successData.footer) && setFooter) {
            setFooter(successData.footer);
        }
    }, [isPreparedData, setFooter, successData]);

    useEffect(() => {
        if (!answerKey) {
            navigate(formUrl);
        }
        if (!isPreparedData) {
            return;
        }
        if (iframe) {
            sendPageInfo('messages-iframe');
            if (successData.results) {
                sendPageInfo('correct-answers-iframe');
            }
            if (successData.total_scores) {
                sendPageInfo('quiz-results-iframe');
            }
            if (successData.redirect?.auto_redirect) {
                sendPageInfo('redirect-iframe');
            }
        }

        if (successData.payment) {
            const fullPaymentUrl = new URL(paymentUrl);
            const paymentParams = new URLSearchParams({
                label: successData.payment.label,
                receiver: successData.payment.receiver,
                sum: String(successData.payment.sum),
                targets: successData.payment.targets,
                successURL: window.location.href,
                ...defaultPaymentParams,
            });
            redirectToExternalUrl(`${fullPaymentUrl}?${paymentParams}`);
        }
    }, [isPreparedData, successData, answerKey, navigate, formUrl]);

    if (!isPreparedData || !answerKey) {
        return (
            // TODO: [ilay] common component?
            <div className={cnSuccessPage('Loader')}>
                <Loader size="l" />
            </div>
        );
    }

    const successMessage = (
        <SuccessMessage
            image={successData.image}
            scores={successData.scores}
            totalScores={successData.total_scores}
            title={successData.title}
            subtitle={successData.subtitle}
            redirect={successData.redirect}
        />
    );

    if (shareResult) {
        return <div className={cnSuccessPage('', {iframe})}>{successMessage}</div>;
    }

    return (
        <div className={cnSuccessPage('', {iframe})}>
            {successMessage}
            {shareOnSuccess && (
                <Share
                    surveyId={surveyId}
                    stats={!isEmpty(successData.stats)}
                    results={successData.results}
                    answerKey={answerKey}
                    scores={successData.scores}
                    totalScores={successData.total_scores}
                    title={successData.title}
                    surveyName={successData.name}
                    imageLink={successData.image?.links?.[QuestionImageSize.width360]}
                />
            )}

            {successData.correct && <Results answerKey={answerKey} />}

            {successData.stats && <Stats stats={successData.stats} />}

            {Boolean(successData.integrations?.length) && <Integrations answerKey={answerKey} />}

            {successData.teaser !== false && !iframe && <Promo />}
        </div>
    );
};

export default SuccessPage;
